import React from "react";

/**
 * Role-Based Access Control (RBAC) System
 *
 * Org Roles (internal team, see all org screens):
 *   super_admin      – Full access; add/edit/delete everything
 *   admin            – View + add/edit; NO delete
 *   read_only        – View only; no add/edit/delete
 *
 * Vendor Roles (vendor-specific; only see vendor screens):
 *   salon_super_admin – Full vendor management for their vendorId
 *   salon_admin       – Vendor view + add/edit; no delete
 */

// --- Permission keys used throughout the app ---
// org
export const PERM_VIEW           = "view";
export const PERM_ADD            = "add";
export const PERM_EDIT           = "edit";
export const PERM_DELETE         = "delete";
export const PERM_MANAGE_ROLES   = "manage_roles";
// vendor scopes (only relevant for vendor roles)
export const SCOPE_VENDOR_BOOKINGS  = "vendor_bookings";
export const SCOPE_VENDOR_STYLISTS  = "vendor_stylists";
export const SCOPE_VENDOR_WORK      = "vendor_work_details";
export const SCOPE_VENDOR_CUSTOMERS = "vendor_customers";

const ROLE_PERMISSIONS = {
  // ── Org roles ──────────────────────────────────────────────
  super_admin: {
    name: "Super Admin",
    description: "Full access to all org screens; add, edit and delete",
    isVendorRole: false,
    permissions: [
      PERM_VIEW, PERM_ADD, PERM_EDIT, PERM_DELETE, PERM_MANAGE_ROLES,
    ],
  },
  admin: {
    name: "Admin",
    description: "View all data; add and edit but no delete",
    isVendorRole: false,
    permissions: [PERM_VIEW, PERM_ADD, PERM_EDIT],
  },
  read_only: {
    name: "Read Only",
    description: "View-only access; no add, edit or delete",
    isVendorRole: false,
    permissions: [PERM_VIEW],
  },

  // ── Vendor roles ────────────────────────────────────────────
  // vendorId is stored separately in the adminUser document
  salon_super_admin: {
    name: "Salon Super Admin",
    description: "Full management of their assigned vendor/salon",
    isVendorRole: true,
    permissions: [
      PERM_VIEW, PERM_ADD, PERM_EDIT, PERM_DELETE,
      SCOPE_VENDOR_BOOKINGS, SCOPE_VENDOR_STYLISTS,
      SCOPE_VENDOR_WORK, SCOPE_VENDOR_CUSTOMERS,
    ],
  },
  salon_admin: {
    name: "Salon Admin",
    description: "Vendor view + add/edit; no delete",
    isVendorRole: true,
    permissions: [
      PERM_VIEW, PERM_ADD, PERM_EDIT,
      SCOPE_VENDOR_BOOKINGS, SCOPE_VENDOR_STYLISTS,
      SCOPE_VENDOR_WORK, SCOPE_VENDOR_CUSTOMERS,
    ],
  },
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Returns the current admin's role from localStorage. */
export const getUserRole = () =>
  localStorage.getItem("adminRole") || "read_only";

/** Returns the current admin's vendorId (only set for vendor roles). */
export const getVendorId = () =>
  localStorage.getItem("adminVendorId") || null;

/** Returns true if the current role is a vendor-specific role. */
export const isVendorRole = () => {
  const role = getUserRole();
  return ROLE_PERMISSIONS[role]?.isVendorRole === true;
};

/** Returns true if the current role is an org-level role. */
export const isOrgRole = () => !isVendorRole();

/** Check if user has a specific permission key. */
export const hasPermission = (permission) => {
  const role = getUserRole();
  const config = ROLE_PERMISSIONS[role];
  if (!config) return false;
  return config.permissions.includes(permission);
};

/** Can the current user add records? */
export const canAdd = () => hasPermission(PERM_ADD);

/** Can the current user edit records? */
export const canEdit = () => hasPermission(PERM_EDIT);

/** Can the current user delete records? */
export const canDelete = () => hasPermission(PERM_DELETE);

/** Can the current user manage roles (access RoleManagement page)? */
export const canManageRoles = () => hasPermission(PERM_MANAGE_ROLES);

/** Legacy helpers kept for backwards compatibility */
export const canManageAdmins = () => hasPermission(PERM_MANAGE_ROLES);
export const canManageUsers  = () => hasPermission(PERM_ADD) || hasPermission(PERM_EDIT);
export const canDeleteData   = () => hasPermission(PERM_DELETE);

/** Is the user any kind of admin (org or vendor)? */
export const isAdmin = () => {
  const r = getUserRole();
  return r === "super_admin" || r === "admin" ||
    r === "salon_super_admin" || r === "salon_admin";
};

/** Is the user a super_admin? */
export const isSuperAdmin = () => getUserRole() === "super_admin";

/** Get full role config object for a given role (defaults to current user). */
export const getRoleDetails = (role = null) => {
  const r = role || getUserRole();
  return ROLE_PERMISSIONS[r] || ROLE_PERMISSIONS["read_only"];
};

/** All role options (for dropdowns). */
export const getAllRoles = () =>
  Object.keys(ROLE_PERMISSIONS).map((key) => ({
    id: key,
    name: ROLE_PERMISSIONS[key].name,
    description: ROLE_PERMISSIONS[key].description,
    isVendorRole: ROLE_PERMISSIONS[key].isVendorRole,
  }));

/** Org-only roles. */
export const getOrgRoles = () =>
  getAllRoles().filter((r) => !r.isVendorRole);

/** Vendor-only roles. */
export const getVendorRoles = () =>
  getAllRoles().filter((r) => r.isVendorRole);

// ── Route protection HOC ─────────────────────────────────────────────────────

/**
 * Wraps a component so it only renders when the user has the given permission.
 * Usage: export default withPermission(PERM_VIEW)(MyComponent)
 */
export const withPermission = (permission) => (Component) => (props) => {
  if (hasPermission(permission)) {
    return <Component {...props} />;
  }
  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <h2>Access Denied</h2>
      <p>You don't have permission to access this page.</p>
    </div>
  );
};

/**
 * Legacy HOC – keeps old call-sites working.
 * requireRole("admin") means the user must be super_admin or admin.
 */
export const requireRole = (requiredRole) => (Component) => (props) => {
  const roleHierarchy = {
    super_admin: 5,
    salon_super_admin: 4,
    admin: 3,
    salon_admin: 2,
    read_only: 1,
  };
  const userLevel = roleHierarchy[getUserRole()] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;

  if (userLevel >= requiredLevel) {
    return <Component {...props} />;
  }
  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <h2>Access Denied</h2>
      <p>Required role: {ROLE_PERMISSIONS[requiredRole]?.name}</p>
    </div>
  );
};

/** Utility used in JSX to guard rendering by role level. */
export const canView = (requiredRole) => {
  const roleHierarchy = {
    super_admin: 5,
    salon_super_admin: 4,
    admin: 3,
    salon_admin: 2,
    read_only: 1,
  };
  return (roleHierarchy[getUserRole()] || 0) >= (roleHierarchy[requiredRole] || 0);
};
